import cx from 'classnames';
import Auth from 'modules/Auth';
import usePendoTracking from 'customHooks/usePendoTracking';
import { DealType, FrontEndType } from 'components/common/types/Deal.types';
import { useDealAndLineActions } from 'components/pages/DealWithLines/DealLines/hooks/useDealAndLineActions';
import useProgrammaticPlannerActions from 'components/pages/Planner/hooks/useProgrammaticPlannerActions';
import Button, { Color, Size } from 'lib/Button';
import { useDispatch, useSelector } from 'react-redux';
import { CampaignPlannerMode } from 'store/dealManagement/types';
import { getCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { toggleCampaignDrawerOpen } from 'store/dealManagement/reducer';
import { getIsCampaignEnded } from 'store/dealManagement/selectors';
import { getCampaignPlannerMode } from 'store/dealManagement/selectors/getCampaignPlannerMode';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import Overview from './Overview';
import CampaignSettings from './CampaignSettings';
import Details from './Details';
import SaveCampaignButton from './SaveCampaignButton';
import ExclusionCriteria from './ExclusionCriteria/ExclusionCriteria';
import CampaignDrawerError from './CampaignDrawerError';

interface CampaignDrawerProps {
  campaignView: FrontEndType;
}

export const CampaignDrawer: React.FC<CampaignDrawerProps> = ({ campaignView }) => {
  const dispatch = useDispatch();
  const { getPendoDataId } = usePendoTracking();
  const { userName } = Auth.getData();

  const { saveDeal: saveDealProgrammaticDealForm } = useDealAndLineActions();
  const { saveDeal: saveDealProgrammaticPlanner } = useProgrammaticPlannerActions();

  const isCampaignEnded = useSelector(getIsCampaignEnded);
  const { isProgrammaticCampaignType, isDirectSalesCampaignType } = useSelector(getCampaignType);
  const isDealTypeNonGuaranteed = useSelector(getDealType) !== DealType.GUARANTEED;

  const isOverviewMode = useSelector(getCampaignPlannerMode) === CampaignPlannerMode.OVERVIEW;

  return (
    <div
      className={cx('w-[var(--campaign-drawer-width)] shrink-0 h-full flex flex-col overflow-auto bg-neutral-200', {
        'pb-[var(--overview-mode-footer-height)]': isOverviewMode,
      })}
      data-test-id="campaign-drawer-open"
    >
      <Overview
        campaignView={campaignView}
        saveDeal={campaignView === FrontEndType.STANDARD ? saveDealProgrammaticDealForm : saveDealProgrammaticPlanner}
        campaignNameOptions={{
          autoFocus: true,
          autoFillOnBlur: isProgrammaticCampaignType,
          campaignNameSuffix: `Campaign by ${userName}`,
        }}
      />
      <Details />
      {isDirectSalesCampaignType && <ExclusionCriteria />}
      {isProgrammaticCampaignType && <CampaignSettings />}
      <div className="flex flex-col gap-4 mx-6 mb-4 mt-auto">
        {isDirectSalesCampaignType && <CampaignDrawerError />}
        {isProgrammaticCampaignType && isDealTypeNonGuaranteed && (
          <Button
            isDisabled={isCampaignEnded}
            size={Size.FIT_CONTAINER}
            color={Color.PRIMARY}
            label="Skip"
            onClick={() => {
              dispatch(toggleCampaignDrawerOpen(false));
            }}
            dataPendoId={getPendoDataId('Bottom Skip Advertiser Brand Category Fields')}
            dataTestId="campaign-drawer-skip"
          />
        )}
        {isDirectSalesCampaignType && <SaveCampaignButton color={Color.PRIMARY} />}
      </div>
    </div>
  );
};
